/**
 * CoreWidgets — Domain-agnostic dashboard widgets (DRR-319)
 *
 * These widgets work for ANY subject type (resident, project, case, account).
 * Domain-specific widgets live in subgraph packages.
 */

import React from 'react';
import {
  Group, Text, Badge, Stack, Paper, Button, Skeleton,
  Progress, Timeline, ActionIcon, ScrollArea
} from '@mantine/core';
import {
  IconCheck,
  IconClock,
  IconPlus,
  IconNote,
  IconClipboardList,
  IconActivity,
  IconChevronRight,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { GqlTaskStatus, GqlTaskPriority } from '@ferp/core/generated/graphql';
import { registerWidget, type WidgetProps } from './DashboardWidgetRegistry';

const GET_TASKS_TODAY = gql`
  query GetTasksToday($tenantNum: Int!, $subjectActorId: String!) {
    tasksBySubject(tenantNum: $tenantNum, subjectActorId: $subjectActorId, dueToday: true) {
      id
      name
      status
      priority
      taskGroupRef
      dueAt
    }
  }
`;

const GET_RECENT_ACTIVITY = gql`
  query GetRecentActivity($tenantNum: Int!, $subjectActorId: String!, $hoursBack: Int) {
    subjectActivityTimeline(tenantNum: $tenantNum, subjectActorId: $subjectActorId, hoursBack: $hoursBack) {
      id
      action
      description
      timestamp
      actorName
      status
    }
  }
`;

export const TasksTodayWidget: React.FC<WidgetProps> = ({
  tenantNum,
  subjectActorId,
  config
}) => {
  const showCompleted = config?.show_completed !== false;
  const groupBy = config?.group_by as string | undefined;

  const { data, loading } = useQuery(GET_TASKS_TODAY, {
    variables: { tenantNum, subjectActorId },
    skip: !tenantNum || !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Stack gap="xs">
        <Skeleton height={20} width="40%" />
        <Skeleton height={32} />
        <Skeleton height={32} />
        <Skeleton height={32} />
      </Stack>
    );
  }

  const tasks = data?.tasksBySubject ?? [];
  const completed = tasks.filter((t: any) => t.status === GqlTaskStatus.COMPLETED);
  const pending = tasks.filter((t: any) => t.status !== GqlTaskStatus.COMPLETED);
  const progress = tasks.length > 0 ? (completed.length / tasks.length) * 100 : 0;

  const displayTasks = showCompleted ? tasks : pending;

  const grouped = groupBy
    ? displayTasks.reduce((acc: Record<string, any[]>, task: any) => {
        const key = task[groupBy] || 'Other';
        if (!acc[key]) acc[key] = [];
        acc[key].push(task);
        return acc;
      }, {})
    : { 'Tasks': displayTasks };

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">Today's Tasks</Text>
        <Badge size="sm" variant="light">
          {completed.length}/{tasks.length}
        </Badge>
      </Group>

      <Progress value={progress} size="sm" color={progress === 100 ? 'green' : 'blue'} />

      <ScrollArea.Autosize mah={200}>
        <Stack gap="xs">
          {Object.entries(grouped).map(([group, groupTasks]: [string, any[]]) => (
            <div key={group}>
              {Object.keys(grouped).length > 1 && (
                <Text size="xs" c="dimmed" mb={4}>{group}</Text>
              )}
              {groupTasks.map((task: any) => (
                <Paper key={task.id} p="xs" withBorder radius="sm">
                  <Group gap="xs">
                    {task.status === GqlTaskStatus.COMPLETED ? (
                      <IconCheck size={14} color="green" />
                    ) : (
                      <IconClock size={14} color="gray" />
                    )}
                    <Text size="sm" td={task.status === GqlTaskStatus.COMPLETED ? 'line-through' : undefined}>
                      {task.name}
                    </Text>
                    {task.priority === GqlTaskPriority.HIGH && (
                      <Badge size="xs" color="red" ml="auto">!</Badge>
                    )}
                  </Group>
                </Paper>
              ))}
            </div>
          ))}
        </Stack>
      </ScrollArea.Autosize>
    </Stack>
  );
};

export const QuickActionsWidget: React.FC<WidgetProps & {
  onAction?: (action: string, subjectActorId: string) => void;
}> = ({
  tenantNum,
  subjectActorId,
  config,
  onAction
}) => {
  const actions = (config?.actions as string[]) ?? ['add_note', 'add_task', 'start_assessment'];

  const actionConfig: Record<string, { label: string; icon: typeof IconPlus; color: string }> = {
    add_note: { label: 'Add Note', icon: IconNote, color: 'blue' },
    add_task: { label: 'Add Task', icon: IconPlus, color: 'green' },
    start_assessment: { label: 'Start Assessment', icon: IconClipboardList, color: 'violet' },
  };

  const handleAction = (action: string) => {
    onAction?.(action, subjectActorId);
  };

  return (
    <Stack gap="xs">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">Quick Actions</Text>
      {actions.map((actionId) => {
        const cfg = actionConfig[actionId];
        if (!cfg) return null;
        return (
          <Button
            key={actionId}
            variant="light"
            color={cfg.color}
            size="xs"
            leftSection={<cfg.icon size={14} />}
            onClick={() => handleAction(actionId)}
            fullWidth
          >
            {cfg.label}
          </Button>
        );
      })}
    </Stack>
  );
};

export const ActivityTimelineWidget: React.FC<WidgetProps> = ({
  tenantNum,
  subjectActorId,
  config
}) => {
  const hoursBack = (config?.hours_back as number) ?? 24;

  const { data, loading } = useQuery(GET_RECENT_ACTIVITY, {
    variables: { tenantNum, subjectActorId, hoursBack },
    skip: !tenantNum || !subjectActorId,
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Stack gap="xs">
        <Skeleton height={20} width="50%" />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  const events = data?.subjectActivityTimeline ?? [];

  if (events.length === 0) {
    return (
      <Stack gap="xs">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">Recent Activity</Text>
        <Text size="sm" c="dimmed">No recent activity</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">Recent Activity</Text>
        <ActionIcon variant="subtle" size="xs">
          <IconChevronRight size={14} />
        </ActionIcon>
      </Group>

      <ScrollArea.Autosize mah={200}>
        <Timeline bulletSize={16} lineWidth={2}>
          {events.slice(0, 5).map((event: any) => (
            <Timeline.Item
              key={event.id}
              bullet={<IconActivity size={10} />}
              title={<Text size="sm">{event.action}</Text>}
            >
              <Text size="xs" c="dimmed">{event.description}</Text>
              <Group gap="xs" mt={4}>
                <Text size="xs" c="dimmed">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </Text>
                {event.actorName && (
                  <Badge size="xs" variant="outline">{event.actorName}</Badge>
                )}
              </Group>
            </Timeline.Item>
          ))}
        </Timeline>
      </ScrollArea.Autosize>
    </Stack>
  );
};

registerWidget('tasks-today', TasksTodayWidget, 'core', 'Tasks due today for subject');
registerWidget('quick-actions', QuickActionsWidget, 'core', 'Context-aware action buttons');
registerWidget('activity-timeline', ActivityTimelineWidget, 'core', 'Recent audit trail');
